import { createStore, Effect } from 'effector';

import { applyReducers } from '@m/effector-utils';
import {
  LocationSubscriptionPlan,
  LocationSubscriptionPlanForm,
  Location,
  WhitelistedPhoneView,
  RateSchedule,
  WhitelistedEmailView,
  AdminLocationJoinQR,
  LocationValidationV2,
  ParkingPassPlan,
} from '@m/types/api';

import {
  getLocations,
  getLocationDetail,
  updateLocation,
  getLocationJoinQrCode,
  updatePlatformSettings,
  getEquipmentlessStatusFx,
  setEquipmentlessStatusFx,
  getLocationSubscriptionPlans,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
  getWhitelistedPhones,
  addWhitelistedPhones,
  addWhitelistedEmails,
  getWhitelistedEmails,
  getLocationValidations,
  updateLocationHours,
  createSiteParkingPassPlan,
  deleteSiteParkingPassPlan,
  getSiteParkingPassPlans,
  updateSiteParkingPassPlan,
} from './actions';

import type { DoneHandler } from '@m/effector-utils';
import type { APIResponse } from '@m/utils/http';

export type State = {
  equipmentlessStatusMap: { [id: number]: boolean };
  joinQrMap: { [id: number]: AdminLocationJoinQR };
  locationDetails: { [id: string]: Location };
  locations: Location[];
  siteParkingPassPlans: { [zoneUuid: string]: ParkingPassPlan[] };
  siteRateSchedules: { [siteId: number]: RateSchedule | null };
  siteSubscriptionPlanViewMap: { [siteId: number]: LocationSubscriptionPlan[] };
  subscriptionPlanForm: LocationSubscriptionPlanForm | null;
  subscriptionPlanWhitelistedEmailMap: { [subPlanId: number]: WhitelistedEmailView[] };
  subscriptionPlanWhitelistedPhoneMap: { [subPlanId: number]: WhitelistedPhoneView[] };
  unattendedStatusMap: { [id: number]: boolean };
  validationListMap: { [id: number]: LocationValidationV2[] };
};

export const initialState: State = {
  locations: [],
  locationDetails: {},
  siteRateSchedules: {},
  siteParkingPassPlans: {},
  subscriptionPlanForm: null,
  siteSubscriptionPlanViewMap: {},
  subscriptionPlanWhitelistedPhoneMap: {},
  subscriptionPlanWhitelistedEmailMap: {},
  joinQrMap: {},
  equipmentlessStatusMap: {},
  unattendedStatusMap: {},
  validationListMap: {},
};

const store = createStore(initialState);

type Reducers = {
  addWhitelistedEmails: {
    action: Effect<Parameters<typeof addWhitelistedEmails>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof addWhitelistedEmails>[0], State>;
  };
  addWhitelistedPhones: {
    action: Effect<Parameters<typeof addWhitelistedPhones>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof addWhitelistedPhones>[0], State>;
  };
  createParkingPassPlan: {
    action: Effect<Parameters<typeof createSiteParkingPassPlan>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof createSiteParkingPassPlan>[0], State>;
  };
  deleteParkingPassPlan: {
    action: Effect<Parameters<typeof deleteSiteParkingPassPlan>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof deleteSiteParkingPassPlan>[0], State>;
  };
  deleteSubscriptionPlan: {
    action: Effect<Parameters<typeof deleteSubscriptionPlan>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof deleteSubscriptionPlan>[0], State>;
  };
  getEquipmentlessStatusFx: {
    action: Effect<Parameters<typeof getEquipmentlessStatusFx>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getEquipmentlessStatusFx>[0], State>;
  };
  getLocationDetail: {
    action: Effect<Parameters<typeof getLocationDetail>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getLocationDetail>[0], State>;
  };
  getLocationJoinQrCode: {
    action: Effect<Parameters<typeof getLocationJoinQrCode>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getLocationJoinQrCode>[0], State>;
  };
  getLocations: {
    action: Effect<Parameters<typeof getLocations>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getLocations>[0], State>;
  };
  getLocationSubscriptionPlans: {
    action: Effect<Parameters<typeof getLocationSubscriptionPlans>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getLocationSubscriptionPlans>[0], State>;
  };
  // ValidationsEnum
  getLocationValidations: {
    action: Effect<Parameters<typeof getLocationValidations>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getLocationValidations>[0], State>;
  };
  // Parking passes
  getParkingPassPlans: {
    action: Effect<Parameters<typeof getSiteParkingPassPlans>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getSiteParkingPassPlans>[0], State>;
  };
  // Whitelisted Emails
  getWhitelistedEmails: {
    action: Effect<Parameters<typeof getWhitelistedEmails>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getWhitelistedEmails>[0], State>;
  };

  // Whitelisted Phones
  getWhitelistedPhones: {
    action: Effect<Parameters<typeof getWhitelistedPhones>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof getWhitelistedPhones>[0], State>;
  };
  setEquipmentlessStatusFx: {
    action: Effect<Parameters<typeof setEquipmentlessStatusFx>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof setEquipmentlessStatusFx>[0], State>;
  };
  updateLocation: {
    action: Effect<Parameters<typeof updateLocation>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof updateLocation>[0], State>;
  };
  // #endregion Fees

  updateLocationHours: {
    action: Effect<Parameters<typeof updateLocationHours>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof updateLocationHours>[0], State>;
  };
  updateParkingPassPlan: {
    action: Effect<Parameters<typeof updateSiteParkingPassPlan>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof updateSiteParkingPassPlan>[0], State>;
  };
  // Platform settings
  updatePlatformSettings: {
    action: Effect<Parameters<typeof updatePlatformSettings>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof updatePlatformSettings>[0], State>;
  };
  updateSubscriptionPlan: {
    action: Effect<Parameters<typeof updateSubscriptionPlan>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof updateSubscriptionPlan>[0], State>;
  };
  // #endregion Lanes
};

export const reducers: Reducers = {
  getLocations: {
    action: getLocations,
    done: (state, { result: { success, data } = {} }) => ({
      ...state,
      locations: success ? data : state.locations,
    }),
  },
  getLocationDetail: {
    action: getLocationDetail,
    done: (state, { params: { id }, result: { success, data } = {} }) =>
      success
        ? {
            ...state,
            locationDetails: {
              ...state.locationDetails,
              [id]: data,
            },
          }
        : state,
  },
  updateLocation: {
    action: updateLocation,
    done: (state, { params: { id }, result: { success, data } = {} }) => ({
      ...state,
      locationDetails: success
        ? {
            ...state.locationDetails,
            [id]: data,
          }
        : state.locationDetails,
    }),
  },
  updateLocationHours: {
    action: updateLocationHours,
    done: (state, { params: { id }, result: { success, data } = {} }) => ({
      ...state,
      locationDetails: success
        ? {
            ...state.locationDetails,
            [id]: data,
          }
        : state.locationDetails,
    }),
  },
  getLocationJoinQrCode: {
    action: getLocationJoinQrCode,
    done: (state, { params: { id }, result: { success, data } = {} }) =>
      success
        ? {
            ...state,
            joinQrMap: {
              ...state.joinQrMap,
              [id]: data,
            },
          }
        : state,
  },
  updatePlatformSettings: {
    action: updatePlatformSettings,
    done: (state, { params: { id }, result: { success, data } = {} }) =>
      success
        ? {
            ...state,
            locationDetails: {
              ...state.locationDetails,
              [id]: data,
            },
          }
        : state,
  },
  getEquipmentlessStatusFx: {
    action: getEquipmentlessStatusFx,
    done: (state, { params: { siteId }, result: { success, data } = {} }) =>
      success
        ? {
            ...state,
            equipmentlessStatusMap: {
              ...state.equipmentlessStatusMap,
              [siteId]: data.equipmentless,
            },
          }
        : state,
  },
  setEquipmentlessStatusFx: {
    action: setEquipmentlessStatusFx,
    done: (state, { params: { siteId, enabled }, result: { success } = {} }) =>
      success
        ? {
            ...state,
            equipmentlessStatusMap: {
              ...state.equipmentlessStatusMap,
              [siteId]: enabled,
            },
          }
        : state,
  },
  getLocationSubscriptionPlans: {
    action: getLocationSubscriptionPlans,
    done: (state, { params: { siteId }, result: { success, data } = {} }) => ({
      ...state,
      siteSubscriptionPlanViewMap: success
        ? {
            ...state.siteSubscriptionPlanViewMap,
            [siteId]: data,
          }
        : state.siteSubscriptionPlanViewMap,
    }),
  },
  updateSubscriptionPlan: {
    action: updateSubscriptionPlan,
    done: (
      state,
      { params: { subscriptionPlanId, subscriptionPlan }, result: { success, data } = {} },
    ) => {
      if (success) {
        return {
          ...state,
          subscriptionPlanForm: data,
        };
      }
      return state;
    },
  },
  deleteSubscriptionPlan: {
    action: deleteSubscriptionPlan,
    done: (state, { result: { success, data } = {} }) => ({
      ...state,
      deleted: data.deleted,
    }),
  },
  getWhitelistedPhones: {
    action: getWhitelistedPhones,
    done: (state, { params: { subscriptionPlanId }, result: { success, data } = {} }) => {
      if (success) {
        return {
          ...state,
          subscriptionPlanWhitelistedPhoneMap: {
            [subscriptionPlanId]: data,
          },
        };
      }
      return state;
    },
  },
  addWhitelistedPhones: {
    action: addWhitelistedPhones,
    done: (
      state,
      { params: { subscriptionPlanId, whitelistedPhoneForm }, result: { success, data } = {} },
    ) => {
      const newPhones = whitelistedPhoneForm.phoneNumbers;
      if (success && newPhones.length > 0) {
        return {
          ...state,
          subscriptionPlanWhitelistedPhoneMap: {
            [subscriptionPlanId]: [
              ...newPhones.map((number) => ({
                id: 0,
                phoneNumber: number,
                addedAt: new Date().getTime(),
              })),
              ...state.subscriptionPlanWhitelistedPhoneMap[subscriptionPlanId],
            ],
          },
        };
      }
      return state;
    },
  },
  getWhitelistedEmails: {
    action: getWhitelistedEmails,
    done: (state, { params: { subscriptionPlanId }, result: { success, data } = {} }) => {
      if (success) {
        return {
          ...state,
          subscriptionPlanWhitelistedEmailMap: {
            [subscriptionPlanId]: data,
          },
        };
      }
      return state;
    },
  },
  addWhitelistedEmails: {
    action: addWhitelistedEmails,
    done: (
      state,
      { params: { subscriptionPlanId, whitelistedEmailForm }, result: { success, data } = {} },
    ) => {
      const newEmails = whitelistedEmailForm.emails;
      if (success) {
        return {
          ...state,
          subscriptionPlanWhitelistedEmailMap: {
            [subscriptionPlanId]: [
              ...newEmails.map((email) => ({
                id: 0,
                email,
                addedAt: new Date().getTime(),
              })),
              ...state.subscriptionPlanWhitelistedEmailMap[subscriptionPlanId],
            ],
          },
        };
      }
      return state;
    },
  },
  getLocationValidations: {
    action: getLocationValidations,
    done: (state, { params: { id }, result: { success, data } = {} }) =>
      success
        ? {
            ...state,
            validationListMap: {
              ...state.validationListMap,
              [id]: data,
            },
          }
        : state,
  },
  // #region Parking Pass Plans
  getParkingPassPlans: {
    action: getSiteParkingPassPlans,
    done: (state, { params: { zoneUuid }, result: { success, data } = {} }) => ({
      ...state,
      siteParkingPassPlans: success
        ? {
            ...state.siteParkingPassPlans,
            [zoneUuid]: data.parkingPassPlans,
          }
        : {
            ...state.siteParkingPassPlans,
          },
    }),
  },
  createParkingPassPlan: {
    action: createSiteParkingPassPlan,
    done: (state, { params: { zoneUuid }, result: { success, data } = {} }) => ({
      ...state,
      siteParkingPassPlans: success
        ? {
            ...state.siteParkingPassPlans,
            [zoneUuid]: [...(state.siteParkingPassPlans[zoneUuid] || []), data.parkingPassPlan],
          }
        : state.siteParkingPassPlans,
    }),
  },
  updateParkingPassPlan: {
    action: updateSiteParkingPassPlan,
    done: (state, { params: { zoneUuid, planId }, result: { success, data } = {} }) => ({
      ...state,
      siteParkingPassPlans: success
        ? {
            ...state.siteParkingPassPlans,
            [zoneUuid]: state.siteParkingPassPlans[zoneUuid].map((plan) =>
              plan.planId === planId ? data.parkingPassPlan : plan,
            ),
          }
        : state.siteParkingPassPlans,
    }),
  },
  deleteParkingPassPlan: {
    action: deleteSiteParkingPassPlan,
    done: (state, { params: { zoneUuid, planId }, result: { success } = {} }) => ({
      ...state,
      siteParkingPassPlans: success
        ? {
            ...state.siteParkingPassPlans,
            [zoneUuid]: state.siteParkingPassPlans[zoneUuid].filter(
              (plan) => plan.planId !== planId,
            ),
          }
        : state.siteParkingPassPlans,
    }),
  },
  // #end Parking Pass Plans
};

export default applyReducers({ storeName: 'admin/location', store, reducers });
